import * as moment from 'moment';
import { DIGITAL_MEMORY_LOG_CONSTANTS } from '../constants/digital-memory-log.constants';

export interface DigitalMemoryLogExpirySource {
  expiryDate?: string | number | Date;
  createdAt?: string | number | Date;
}

function parseMoment(value: string | number | Date): moment.Moment | null {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    const strictParsed = moment(
      value,
      [moment.ISO_8601, 'DD-MM-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss.SSSZ'],
      true
    );
    if (strictParsed.isValid()) {
      return strictParsed;
    }
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
}

/**
 * Build expiryDate as baseDate + EXPIRY_YEARS (defaults to now when baseDate is missing).
 */
export function buildDigitalMemoryLogExpiryDate(baseDate?: string | number | Date): string {
  const base = parseMoment(baseDate) || moment();
  return base
    .clone()
    .add(DIGITAL_MEMORY_LOG_CONSTANTS.DEFAULTS.EXPIRY_YEARS, 'years')
    .toISOString();
}

/**
 * Set expiryDate to createdAt + 1 year when no valid expiryDate exists.
 */
export function enrichDigitalMemoryLogExpiry(entity: DigitalMemoryLogExpirySource): void {
  if (!entity) {
    return;
  }

  if (parseMoment(entity.expiryDate)) {
    return;
  }

  const createdAt = parseMoment(entity.createdAt);
  if (!createdAt) {
    return;
  }

  entity.expiryDate = buildDigitalMemoryLogExpiryDate(createdAt.toISOString());
}

export function getDigitalMemoryLogExpiryDate(entity: DigitalMemoryLogExpirySource): Date | null {
  const expiry = getDigitalMemoryLogExpiryMoment(entity);
  return expiry ? expiry.toDate() : null;
}

export function getDigitalMemoryLogExpiryMoment(entity: DigitalMemoryLogExpirySource): moment.Moment | null {
  if (!entity) {
    return null;
  }

  enrichDigitalMemoryLogExpiry(entity);
  return parseMoment(entity.expiryDate);
}

export function isDigitalMemoryLogExpired(entity: DigitalMemoryLogExpirySource): boolean {
  const expiryDate = getDigitalMemoryLogExpiryMoment(entity);
  if (!expiryDate) {
    return false;
  }

  return moment().startOf('day').isAfter(expiryDate.startOf('day'));
}
